import queryString from "query-string";
import React, { useEffect } from "react";
import { Alert, Card, ListGroup } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router";

import * as api from "../../api/api";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import Layout, { Title } from "../../components/Layout/Layout";
import LoadMoreButton from "../../components/LoadMoreButton";
import MissionFilters from "../../components/MissionFilters/index.jsx";
import MissionGroup, { groupMissions } from "../../components/MissionGroup";
import MissionListItem from "../../components/MissionListItem";
import { useUser } from "../../contexts/user_provider";
import urls from "../../urls.jsx";

function MissionList({ missions }) {
    if (!missions || missions.length === 0) {
        return (
            <Alert variant="primary" className="mt-0 m-4">
                No missions found
            </Alert>
        );
    }

    const groups = groupMissions(missions);

    return (
        <ListGroup variant="flush">
            {groups.map((group) => {
                if (group.length === 1) {
                    const mission = group[0];
                    return (
                        <MissionListItem
                            key={`mission-${mission.id}`}
                            mission={mission}
                            timeDisplayFormat="YYYY-MM-DD HH:mm:ss"
                            showOrganizationName={false}
                        />
                    );
                } else {
                    return (
                        <MissionGroup
                            key={`mission-group-${group.map((mission) => mission.id).join("-")}`}
                            missions={group}
                            timeDisplayFormat="YYYY-MM-DD HH:mm:ss"
                            showOrganizationName={false}
                        />
                    );
                }
            })}
        </ListGroup>
    );
}

export default function MissionListPage() {
    const missionsPerPage = 50;

    const { organizationId, locationId } = useParams();
    const location = useLocation();
    const queryParams = queryString.parse(location.search);

    const [filters, setFilters] = React.useState({
        showDeleted: queryParams.showDeleted === "true",
        droneIds: queryParams.droneIds
            ? queryParams.droneIds.split(",").map((id) => id.toUpperCase())
            : [],
        startDate: queryParams.startDate
            ? new Date(Number(queryParams.startDate))
            : undefined,

        endDate: queryParams.endDate
            ? new Date(Number(queryParams.endDate))
            : undefined,
    });

    const [missionsToLoad, setMissionsToLoad] = React.useState(missionsPerPage);
    const [droneConfigs] = api.useDroneConfigsFromLocation(
        organizationId,
        locationId,
        { type: "map" },
    );
    const locationDroneIds = Object.keys(droneConfigs);

    const [missions, isLoading] = api.useMissionsFromLocation(
        organizationId,
        locationId,
        missionsToLoad,
        filters.showDeleted,
        filters.droneIds,
        filters.startDate,
        filters.endDate,
    );

    const navigate = useNavigate();
    const { getUserIsAdmin } = useUser();

    useEffect(() => {
        if (!locationId || locationId === "undefined") {
            navigate(urls.locations(organizationId), { replace: true });
        }
    }, [locationId, organizationId, navigate]);

    React.useEffect(() => {
        // Skip this effect to let the other effect handle it
        if (!locationId || locationId === "undefined") return;

        const newQuery = {
            showDeleted: filters.showDeleted ? "true" : undefined,
            droneIds:
                filters.droneIds.length > 0
                    ? filters.droneIds.join(",")
                    : undefined,
            startDate: filters.startDate
                ? filters.startDate.getTime()
                : undefined,
            endDate: filters.endDate ? filters.endDate.getTime() : undefined,
        };

        // Remove undefined keys to clean up the URL
        const cleanedQuery = Object.fromEntries(
            Object.entries(newQuery).filter(([, v]) => v !== undefined),
        );

        const newSearch = queryString.stringify(cleanedQuery);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });
    }, [filters, location.pathname, locationId, navigate]);

    return (
        <Layout>
            <title>Missions | MyCorvus</title>
            <Breadcrumbs path={breadcrumbPaths.missionList} />
            <Card>
                <Title
                    title={"Missions"}
                    backButtonUrl={urls.location(organizationId, locationId)}
                />
                <MissionFilters
                    className={"mx-3 mb-3"}
                    filters={filters}
                    setFilters={setFilters}
                    droneOptions={locationDroneIds}
                    showCorvusToggle={false}
                    showDeletedToggle={getUserIsAdmin()}
                />
                <MissionList missions={missions} />
            </Card>
            <LoadMoreButton
                disabled={missionsToLoad > missions.length}
                isLoading={isLoading}
                setIsLoading={() => {}}
                load={() => {
                    setMissionsToLoad(missionsToLoad + missionsPerPage);
                }}
                showLoadAll={false}
                loadAll={() => {}}
                text={`Showing ${missions.length} missions`}
            />
        </Layout>
    );
}
